import React, { useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Form, Input, Row, Col, DatePicker, Select } from "antd";
import styled from "@emotion/styled";
import moment from "moment";
import { momentDate } from "utils/Date";
import {
  NON_EMPTY_REGEX,
  NON_CHARACTER,
  VALIDATE_CHARACTER,
  VALIDATE_SYMBOL_CHARACTER,
} from "common/Constants";
import { getCountryCompanyList } from "redux/selectors";
import LocationMapPickerModal from "molecules/LocationMapPickerModal";

const Styleddiv = styled.div`
  .ant-form-item-label > label {
    font-weight: bold;
  }
`;

const EditInformation = ({
  title,
  details,
  setValues,
  viewOnly,
  setsubmit,
  alldatas,
}) => {
  const [form] = Form.useForm();
  const CountryCompany = useSelector(getCountryCompanyList);

  // Re-run the init effect only when the *content* of details changes,
  // not when the parent passes a new object reference on re-render.
  const detailsKey = useMemo(() => JSON.stringify(details || {}), [details]);

  useEffect(() => {
    const {
      name = title === "Add Location"
        ? details?.location_name
        : title === "holiday"
          ? details?.holiday_name
          : title === "break"
            ? details?.breakName
            : details?.department_name,
      location_latitude = details?.latitude,
      location_longitude = details?.longitude,
      start_date = details?.start_date,
      end_date = details?.end_date,
      country = details?.country,
    } = details || {};

    form.setFieldsValue({
      name,
      country,
      location_latitude,
      location_longitude,
      start_date: start_date
        ? moment(start_date, ["YYYY-MM-DD", "DD-MM-YYYY"])
        : null,
      end_date: end_date
        ? moment(end_date, ["YYYY-MM-DD", "DD-MM-YYYY"])
        : null,
    });

    setValues((prev) => ({
      ...prev,
      name,
      country,
      location_latitude,
      location_longitude,
      start_date: start_date ?? prev?.start_date ?? null,
      end_date: end_date ?? prev?.end_date ?? null,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsKey]);

  const containerRef = useRef(null);

  const validateDateRange = (date) => {
    const endDate = moment(form.getFieldValue("end_date"));
    if (endDate < date) {
      form.setFieldsValue({ end_date: date });
    }
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    setValues((prev) => ({
      ...prev,
      name: allValues.name,
      country: allValues.country,
      location_latitude: allValues.location_latitude,
      location_longitude: allValues.location_longitude,
      // if field not touched, keep previous (don't fall back to today)
      start_date: allValues.start_date
        ? moment(allValues.start_date).format("YYYY-MM-DD")
        : prev?.start_date ?? null,
      end_date: allValues.end_date
        ? moment(allValues.end_date).format("YYYY-MM-DD")
        : prev?.end_date ?? null,
    }));
    setsubmit(true);
  };

  const handleMapLocationChange = ({
    latitude,
    longitude,
    locationName,
    country,
  }) => {
    // Fields used by the form
    const formPatch = {
      location_latitude: String(latitude),
      location_longitude: String(longitude),
    };
    if (country) formPatch.country = country;
    if (locationName) formPatch.name = locationName;

    // Update the form UI
    form.setFieldsValue(formPatch);
    form
      .validateFields(["location_latitude", "location_longitude", "country"])
      .catch(() => { });

    // Also keep the keys the save API / details object use
    const valuesPatch = {
      ...formPatch,
      latitude: String(latitude),
      longitude: String(longitude),
    };
    if (locationName) valuesPatch.location_name = locationName;

    // Update parent values first, then enable save
    setValues((prev) => ({ ...prev, ...valuesPatch }));
    setsubmit(true);
  };

  return (
    <Styleddiv>
      <Form
        form={form}
        name="AddDepartment"
        layout="vertical"
        disabled={viewOnly}
        initialValues={{
          name: "",
          location_latitude: "",
          location_longitude: "",
          start_date: null,
          end_date: null,
          country: "",
        }}
        onValuesChange={handleFormValuesChange}
      >
        <div className="styled-card">
          {title !== "Add Location" && (
            <Form.Item
              label={
                title === "holiday"
                  ? "Holiday Name"
                  : title === "break"
                    ? "Break Name"
                    : "Team Name"
              }
              name="name"
              rules={[
                {
                  required: true,
                  message:
                    title === "holiday"
                      ? "Holiday Name is required"
                      : title === "break"
                        ? "Break Name is required"
                        : "Team Name is required",
                },
                {
                  validator: (rule, value) => {
                    if (value && !VALIDATE_SYMBOL_CHARACTER.test(value)) {
                      return Promise.reject(
                        title === "holiday"
                          ? "Holiday Name should not contain special characters"
                          : title === "break"
                            ? "Break Name should not contain Numbers"
                            : "Team Name should not contain Numbers"
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={
                  title === "holiday"
                    ? "Holiday name"
                    : title === "break"
                      ? "Break Name"
                      : "Team Name"
                }
                maxLength={75}
              />
            </Form.Item>
          )}

          {title === "Add Location" && (
            <>
              <Row gutter={16}>
                <Col xs={24} md={12} ref={containerRef}>
                  <Form.Item
                    name="country"
                    label="Country/ Company"
                    rules={[
                      {
                        required: true,
                        message: "Please select Country/ Company",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Search to Select"
                      optionFilterProp="children"
                      disabled
                    >
                      {CountryCompany?.map((value, index) => (
                        <Select.Option
                          key={index}
                          value={value.country}
                          label={value.country}
                        >
                          {value.country}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="name"
                    label="Location"
                    rules={[
                      { required: true, message: "Please Enter Location" },
                      {
                        validator: (_, value) => {
                          const selectedCountry = form.getFieldValue("country");

                          if (value && selectedCountry) {
                            const isDuplicate = alldatas?.some(
                              (data) =>
                                data.location_id !== details?.location_id &&
                                data.country.toLowerCase().trim() ===
                                selectedCountry.toLowerCase().trim() &&
                                data.location_name.toLowerCase().trim() ===
                                value.toLowerCase().trim()
                            );

                            if (isDuplicate) {
                              return Promise.reject(
                                "This location already exists for the selected country"
                              );
                            }
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter Location"
                      disabled
                      suffix={
                        details?.login_from_anywhere != 1 && (
                          <LocationMapPickerModal
                            lat={details?.latitude}
                            lng={details?.longitude}
                            countryOptions={CountryCompany?.map(
                              (item) => item.country
                            )}
                            disabled={viewOnly}
                            onLocationChange={handleMapLocationChange}
                          />
                        )
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              {details?.login_from_anywhere != 1 && (
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Latitude"
                      name="location_latitude"
                      rules={[
                        {
                          required: true,
                          pattern: NON_CHARACTER,
                          message: "Latitude is required",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const num = Number(value);
                            if (Number.isNaN(num) || num < -90 || num > 90) {
                              return Promise.reject(
                                "Latitude must be a valid value between -90 and 90"
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Enter latitude" maxLength={20} disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Longitude"
                      name="location_longitude"
                      rules={[
                        {
                          required: true,
                          pattern: NON_CHARACTER,
                          message: "Longitude is required",
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const num = Number(value);
                            if (Number.isNaN(num) || num < -180 || num > 180) {
                              return Promise.reject(
                                "Longitude must be a valid value between -180 and 180"
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Enter longitude" maxLength={20} disabled />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}

          {title === "holiday" && (
            <Row>
              <Col xs={24} xl={24} ref={containerRef}>
                <Form.Item
                  label="Start date"
                  name="start_date"
                  rules={[
                    {
                      required: true,
                      pattern: NON_EMPTY_REGEX,
                      message: "Start Date is required",
                    },
                    () => ({
                      validator(rule, value) {
                        const date = moment(value);
                        return date.isValid()
                          ? Promise.resolve()
                          : Promise.reject();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    className="w-100"
                    format="DD-MM-YYYY"
                    onChange={validateDateRange}
                    placeholder="Select start date"
                    getPopupContainer={() => containerRef.current}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.start_date !== currentValues.start_date
                  }
                >
                  {() => (
                    <Form.Item
                      label="End date"
                      name="end_date"
                      rules={[
                        {
                          required: true,
                          pattern: NON_EMPTY_REGEX,
                          message: "End Date is required",
                        },
                        () => ({
                          validator(rule, value) {
                            const date = moment(value);
                            return date.isValid()
                              ? Promise.resolve()
                              : Promise.reject();
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        className="w-100"
                        format="DD-MM-YYYY"
                        placeholder="Select end date"
                        getPopupContainer={() => containerRef.current}
                        disabledDate={(current) => {
                          const startDate = form.getFieldValue("start_date");
                          return (
                            current &&
                            startDate &&
                            current.isBefore(moment(startDate).startOf("day"))
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>
      </Form>
    </Styleddiv>
  );
};

export default EditInformation;